import axios, { AxiosResponse } from "axios";
import React, { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Listing from "../common/models/Listing";
import MasterCopy from "../common/models/MasterCopy";
import SongCollection from "../common/models/SongCollections";
import SimpleButton from "../components/Buttons/SimpleButton";
import MasterCopyCard, { CollectionCard } from "../components/Card/SongCard";
import Column from "../components/Column/Column";
import { Modal } from "../components/Modal/Modal";
import { NavBar } from "../components/NavBar";
import Row from "../components/Row/Row";
import Text from "../components/Text/Text";
import license_success_icon from "../imgs/license_success.svg";
import search_icon from "../imgs/search_icon.svg";
import search_icon_light from "../imgs/search_icon_light.svg";
import no_results_img from "./imgs/no_results_img.svg";

interface MarketplacePageProps {
  theme: string;
  toggleTheme: any;
}

const MarketPlaceContainer = styled("div")<{ theme: string }>`
  background: #000d30;
  background-size: cover;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  color: white;
  padding: 4%;
  padding-top: 7%;

  @media screen and (max-width: 650px) {
    padding-top: 20%;
  }
`;

const MarketPlaceSongContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: end;
  gap: 2.5%;
  flex-wrap: wrap;
`;

interface SearchInputProps {
  onTextChange: (text: string) => void;
  theme: string;
  currentText: string;
}

const SearchButton = styled(SimpleButton)`
  position: absolute;
  right: 0;
  border-radius: 5px;
  padding: 1.2rem 2.5rem;

  @media screen and (max-width: 650px) {
    display: none;
  }
`;

const SearchBarContainer = styled("div")`
  align-self: center;
  width: 50%;
  display: flex;
  flex-direction: row;
  border: 0.5px solid #374669;
  border-radius: 5px;
  background: ${(props) =>
    props.theme.themeType === "dark" ? "rgba(196, 196, 196, 0.1)" : "white"};
  border: ${(props) =>
    props.theme.themeType === "dark"
      ? "0.5px solid #FFFFFF40"
      : "1px solid #7B80FF;"};
  box-shadow: ${(props) =>
    props.theme.themeType === "dark"
      ? "inset 30.9667px -30.9667px 30.9667px rgba(149, 149, 149, 0.1), inset -30.9667px 30.9667px 30.9667px rgba(255, 255, 255, 0.1)"
      : ""};
  backdrop-filter: blur(61.9333px);
  border-radius: 8px;
  align-items: center;
  overflow: hidden;
  padding: 10px;

  @media screen and (max-width: 650px) {
    width: 90%;
    display: none;
  }
`;

const SearchIcon = styled.img`
  padding-left: 20px;

  @media screen and (max-width: 650px) {
    width: 7%;
    height: 7%;
  }
`;

const SearchBar = styled("input")`
  outline: none;
  border: none;
  background: none;
  font-size: 1em;
  padding: 0.5em;
  color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};
  flex: auto 1 1;
  width: 100%;
  background: none;
  background-color: transparent;
  font-family: "Spartan";

  &::placeholder {
    /* Chrome, Firefox, Opera, Safari 10.1+ */

    color: ${(props) => (props.theme.themeType === "dark" ? "white" : "black")};
    opacity: 1; /* Firefox */

    @media screen and (max-width: 650px) {
      font-size: 0.75em;
    }
  }
`;

const SearchInput = ({
  onTextChange,
  theme,
  currentText,
}: SearchInputProps) => {
  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onTextChange(event.target.value);
  };

  return (
    <>
      <SearchBarContainer>
        <SearchIcon src={theme === "dark" ? search_icon : search_icon_light} />
        <SearchBar
          value={currentText}
          onChange={onInputChange}
          placeholder={"Search by artist, song, or other keywords"}
        />
        <SearchButton>Search</SearchButton>
      </SearchBarContainer>
    </>
  );
};

const TrendingText = styled(Text)`
  font-size: 24px;
`;

interface MarketplaceState {
  showLicenseSuccessModal?: boolean;
  searchQuery?: string;
}

interface LicenseSuccessModalProps {
  show: boolean;
  showModalToggle: any;
  theme: string;
}

const LicenseSuccessModalContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: #000d30;
  min-width: 30%;
  padding-inline: 5%;
  padding-block: 3%;
  border-radius: 20px;
`;
const NoResultsColumn = styled(Column)`
  align-items: center;
  margin-left: auto;
  margin-right: auto;
  margin-top: 5%;
`;

const LicenseSuccessModal = React.forwardRef<
  HTMLDivElement,
  LicenseSuccessModalProps
>(({ theme, show, showModalToggle }, ref) => {
  const onCloseClick = () => {
    showModalToggle(false);
  };

  return (
    <Modal ref={ref} show={show}>
      <LicenseSuccessModalContainer>
        <Text>
          Congrats! You are all set.
          <br />
          Users can license your music through the market now.
        </Text>
        <img src={license_success_icon} />
        <SimpleButton
          style={{ padding: "0.75rem 3rem" }}
          onClick={onCloseClick}
        >
          OK
        </SimpleButton>
      </LicenseSuccessModalContainer>
    </Modal>
  );
});

const Header = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 3%;
  margin-top: 3%;
  margin-bottom: 1%;
  border-bottom: 1px solid white;

  @media screen and (max-width: 650px) {
    overflow-x: scroll;
    margin-bottom: 5%;
  }
`;

const HeaderSection = styled("div")<{ active?: boolean }>`
  font-size: 24px;
  color: ${(props) => (props.active ? "white" : "rgba(255, 255, 255, 0.4)")};
  text-align: center;
  padding-inline: 3%;
  border-bottom: ${(props) =>
    props.active ? "4px solid white" : "rgba(255, 255, 255, 0.4)"};

  &:hover {
    cursor: pointer;
  }

  @media screen and (max-width: 650px) {
    font-size: 18px;
    white-space: nowrap;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;

  @media screen and (max-width: 650px) {
    font-size: 36px;
  }
`;

export const MarketPlacePage: React.FC<MarketplacePageProps> = ({
  theme,
  toggleTheme,
}) => {
  const [songsForSale, setSongsForSale] = useState<Array<MasterCopy>>();
  const [originalMasterCopies, setOriginalMasterCopies] =
    useState<Array<MasterCopy>>();
  const [collections, setCollections] = useState<Array<SongCollection>>();
  const [originalCollections, setOriginalCollections] =
    useState<Array<SongCollection>>();
  const [released, setReleased] = useState<Array<SongCollection>>();
  const [newDrops, setNewDrops] = useState<Array<SongCollection>>();
  const [songsWithLicenses, setSongsWithLicenses] =
    useState<Array<MasterCopy>>();
  const [showMasterCopies, setShowMasterCopies] = useState(true);
  const [listings, setListings] = useState<Array<Listing>>();
  const [searchQuery, setSearchQuery] = useState("");
  const ref = useRef<any>();
  const { state } = useLocation();
  const navigate = useNavigate();
  const locationState = state as MarketplaceState;
  const [showLicenseModal, setLicenseSuccessModal] = useState(false);
  const [selectedSection, setSelectedSection] = useState("All");

  useEffect(() => {
    if (locationState !== null && locationState.showLicenseSuccessModal) {
      setLicenseSuccessModal(true);
      // Replace the current state with null so that we don't see the modal again
      navigate(window.location.pathname, {});
    }
  }, []);

  useEffect(() => {
    if (
      locationState !== null &&
      locationState.searchQuery !== null &&
      locationState.searchQuery !== undefined
    ) {
      setSearchQuery(locationState.searchQuery);
      // Replace the current state with null so that we don't see the modal again
      // navigate(location.pathname, {});
    }
  }, []);

  const onSongClick = (song: MasterCopy) => {
    navigate(`/song/${song.id}`);
  };

  const onCollectionClick = (song: SongCollection) => {
    navigate(`/collection/${song.id}`);
  };

  useEffect(() => {
    axios
      .get(process.env.REACT_APP_BACKEND_URL + "/get_songs_for_sale")
      .then((response: AxiosResponse) => {
        const songs = response.data.songs as Array<MasterCopy>;
        setSongsForSale(songs);
        setOriginalMasterCopies(songs);
        setSongsWithLicenses(
          songs.filter((song) => {
            return song.license_address !== null;
          })
        );
      });
  }, []);

  useEffect(() => {
    axios
      .get
      (process.env.REACT_APP_BACKEND_URL + "/get_all_song_collections")
      .then((response: AxiosResponse) => {
        const collections = response.data
          .song_collections as Array<SongCollection>;
        setOriginalCollections(collections);
        setCollections(collections);
        setReleased(
          collections.filter((collection) => {
            return collection.for_secondary_sale;
          })
        );
        setNewDrops(
          collections.filter((collection) => {
            return !collection.for_secondary_sale;
          })
        );
      });
  }, []);

  useEffect(() => {
    songsForSale &&
      axios
        .post(
          process.env.REACT_APP_BACKEND_URL + "/get_listings",
          JSON.stringify({
            ids: songsForSale.map((song) => {
              return song.active_listing_id;
            }),
          }),
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
        .then((response) => {
          setListings(Listing.fromResponse(response));
        });
  }, [songsForSale]);

  const onTextChange = (text: string) => {
    setSearchQuery(text);
  };

  const search = (songs: Array<MasterCopy>) => {
    return songs.filter(
      (song) =>
        song.song_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        song.artist_stage_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
    );
  };

  const searchCollections = (songs: Array<SongCollection>) => {
    return songs.filter(
      (song) =>
        song.song_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim()) ||
        song.artist_stage_name
          .toLowerCase()
          .includes(searchQuery.toLowerCase().trim())
    );
  };

  return (
    <div style={{background: '#000d30'}}>
      <NavBar theme={theme} themeToggler={toggleTheme} />
      <MarketPlaceContainer theme={theme}>
        <Row align={"space-between"} style={{flexWrap: 'wrap'}}>
          <PageTitle>Marketplace</PageTitle>
          <SearchInput
            theme={theme}
            onTextChange={onTextChange}
            currentText={searchQuery}
          />
        </Row>

        <Header>
          <HeaderSection
            active={selectedSection === "All"}
            onClick={() => {
              setSelectedSection("All");
              setCollections(originalCollections);
              setShowMasterCopies(true);
              setSongsForSale(originalMasterCopies);
            }}
          >
            All
          </HeaderSection>
          <HeaderSection
            active={selectedSection === "New Drops"}
            onClick={() => {
              setSelectedSection("New Drops");
              setCollections(newDrops);
              setShowMasterCopies(false);
            }}
          >
            New Drops
          </HeaderSection>
          <HeaderSection
            active={selectedSection === "Released"}
            onClick={() => {
              setSelectedSection("Released");
              setCollections(released);
              setShowMasterCopies(false);
            }}
          >
            Released
          </HeaderSection>
          <HeaderSection
            active={selectedSection === "Master Copies"}
            onClick={() => {
              setSelectedSection("Master Copies");
              setCollections([]);
              setShowMasterCopies(true);
              setSongsForSale(originalMasterCopies);
            }}
          >
            Master Copies
          </HeaderSection>
          <HeaderSection
            active={selectedSection === "Licenses"}
            onClick={() => {
              setSelectedSection("Licenses");
              setShowMasterCopies(true);
              setCollections([]);
              setSongsForSale(songsWithLicenses);
            }}
          >
            Licenses
          </HeaderSection>
        </Header>

        <MarketPlaceSongContainer>
          {collections && searchCollections(collections).length > 0 && (
            <>
              {collections &&
                searchCollections(collections).map((collection, index) => (
                  <CollectionCard
                    collection={collection}
                    key={index}
                    onCollectionClick={onCollectionClick}
                  />
                ))}
            </>
          )}

          {songsForSale && showMasterCopies && search(songsForSale).length > 0 && (
            <>
              {songsForSale &&
                listings &&
                search(songsForSale).map((song, index) => (
                  <MasterCopyCard
                    song={song}
                    key={index}
                    onSongClick={onSongClick}
                    listing={
                      listings.filter((listing) => {
                        return listing.master_copy_id === song.id;
                      })[0]
                    }
                  />
                ))}
            </>
          )}
        </MarketPlaceSongContainer>

        {songsForSale &&
          search(songsForSale).length === 0 &&
          collections &&
          searchCollections(collections).length === 0 && (
            <>
              <NoResultsColumn>
                <Text style={{textAlign: 'center'}}>Sorry, we don't have this NFT yet <br/> Be the first to create one.</Text>
                <img src={no_results_img} />
              </NoResultsColumn>
              <Column>
                <TrendingText bold={true}>You May Also Like</TrendingText>
                <MarketPlaceSongContainer>
                  {collections.map((collection, index) => (
                    <CollectionCard
                      collection={collection}
                      key={index}
                      onCollectionClick={onCollectionClick}
                    />
                  ))}
                  {songsForSale.map((song, index) => (
                    <MasterCopyCard
                      song={song}
                      key={index}
                      onSongClick={onSongClick}
                      listing={
                        listings.filter((listing) => {
                          return listing.master_copy_id === song.id;
                        })[0]
                      }
                    />
                  ))}
                </MarketPlaceSongContainer>
              </Column>
            </>
          )}

        {showLicenseModal && (
          <LicenseSuccessModal
            theme={theme}
            ref={ref}
            show={showLicenseModal}
            showModalToggle={setLicenseSuccessModal}
          />
        )}
        <Outlet />
      </MarketPlaceContainer>
    </div>
  );
};
